import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Tabs, Tab } from "@material-ui/core";
import { EditOutlined } from "@material-ui/icons";
import { getFinancialEntries, getPaymentChallans, getFeeCalculations } from "../../service";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import PaymentChallansTab from "./PaymentChallansTab";
import FeesCalculationTab from "./FeesCalculationTab";
import EditFeeCalculationDialog from "./EditFeeCalculationDialog";
import { safeDateFormat } from "Clients/ACCB/utils";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "10px",
    },
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    marginBottom: "1rem",
    gap: "15px",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchField: {
    width: "100%",
    maxWidth: 350,
  },
  // newEntryBtn: {
  //   borderRadius: "0.5rem",
  //   textTransform: "none",
  //   fontWeight: 600,
  //   padding: "0.5rem 1.5rem",
  // },
  tableHeadRow: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "12px 10px",
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableBodyCell: {
    padding: "12px 10px",
    fontSize: "0.875rem",
  },
  errorText: {
    color: theme.palette.error.main,
    padding: "1rem",
  },
}));

export default function FinancialDashboard({ onCreate, onCreateChallan, onEdit }) {
  const classes = useStyles();

  const [tab, setTab] = useState("manual"); // "manual" | "challans" | "fee-calculation"
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Payment challans tab state
  const [challans, setChallans] = useState([]);
  const [challansLoading, setChallansLoading] = useState(false);
  const [challansError, setChallansError] = useState("");
  const [challansPage, setChallansPage] = useState(0);
  const [challansRowsPerPage, setChallansRowsPerPage] = useState(10);
  const [challansTotalCount, setChallansTotalCount] = useState(0);

  // Fees calculation tab state
  const [feeCalcs, setFeeCalcs] = useState([]);
  const [feeCalcsLoading, setFeeCalcsLoading] = useState(false);
  const [feeCalcsError, setFeeCalcsError] = useState("");
  const [feeCalcsPage, setFeeCalcsPage] = useState(0);
  const [feeCalcsRowsPerPage] = useState(10);
  const [feeCalcsTotalCount, setFeeCalcsTotalCount] = useState(0);
  const [editingFeeCalc, setEditingFeeCalc] = useState(null);

  const loadFinancialData = () => {
    setLoading(true);
    setError("");
    getFinancialEntries({ page: page + 1, page_size: rowsPerPage, search: debouncedSearch })
      .then((res) => {
        // If paginated response
        if (res.data && res.data.results) {
          setResults(res.data.results);
          setTotalCount(res.data.count || 0);
        } else if (Array.isArray(res.data)) {
          setResults(res.data);
          setTotalCount(res.data.length);
        } else {
          setResults([]);
          setTotalCount(0);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading financial entries:", err);
        setError("Failed to fetch financial entries. Please try again.");
        setLoading(false);
      });
  };

  useEffect(() => {
    if (tab === "manual") loadFinancialData();
  }, [tab, page, rowsPerPage, debouncedSearch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadPaymentChallans = () => {
    setChallansLoading(true);
    setChallansError("");
    getPaymentChallans({ page: challansPage + 1, page_size: challansRowsPerPage, search: debouncedSearch })
      .then((res) => {
        if (res.data && res.data.results) {
          setChallans(res.data.results);
          setChallansTotalCount(res.data.count || 0);
        } else if (Array.isArray(res.data)) {
          setChallans(res.data);
          setChallansTotalCount(res.data.length);
        } else {
          setChallans([]);
          setChallansTotalCount(0);
        }
      })
      .catch(() => {
        setChallans([]);
        setChallansError("Failed to fetch payment challans. Please try again.");
      })
      .finally(() => setChallansLoading(false));
  };

  useEffect(() => {
    if (tab === "challans") loadPaymentChallans();
  }, [tab, challansPage, challansRowsPerPage, debouncedSearch]);

  const handleChallansChangePage = (event, newPage) => {
    setChallansPage(newPage);
  };

  const loadFeeCalculations = () => {
    setFeeCalcsLoading(true);
    setFeeCalcsError("");
    getFeeCalculations({
      page: feeCalcsPage + 1,
      page_size: feeCalcsRowsPerPage,
      search: debouncedSearch,
    })
      .then((res) => {
        if (res.data && res.data.results) {
          setFeeCalcs(res.data.results);
          setFeeCalcsTotalCount(res.data.count || 0);
        } else if (Array.isArray(res.data)) {
          setFeeCalcs(res.data);
          setFeeCalcsTotalCount(res.data.length);
        } else {
          setFeeCalcs([]);
          setFeeCalcsTotalCount(0);
        }
      })
      .catch((err) => {
        console.error("Error loading fee calculations:", err);
        setFeeCalcs([]);
        setFeeCalcsError("Failed to fetch fee calculations. Please try again.");
      })
      .finally(() => setFeeCalcsLoading(false));
  };

  useEffect(() => {
    if (tab === "fee-calculation") loadFeeCalculations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, feeCalcsPage, feeCalcsRowsPerPage, debouncedSearch]);

  const handleFeeCalcsChangePage = (event, newPage) => {
    setFeeCalcsPage(newPage);
  };

  const handleFeeCalcSaved = () => {
    setEditingFeeCalc(null);
    loadFeeCalculations();
  };



  const formatCurrency = (val) => {
    if (val === undefined || val === null || isNaN(parseFloat(val))) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(val);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
      </Box>

      <Box className={classes.toolbar}>
        <GlobalSearch
          className={classes.searchField}
          placeholder="Search Case ID, Challan, or Receiver..."
          value={debouncedSearch}
          onSearchChange={(val) => {
            setDebouncedSearch(val);
            setPage(0);
            setChallansPage(0);
            setFeeCalcsPage(0);
          }}
        />
        <Box style={{ display: "flex", gap: "0.5rem" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onCreate}
            size="small"
          >
            Generate Payment Challan
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={onCreateChallan}
            size="small"
          >
            Create Payment Challan
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        style={{ marginBottom: "1rem" }}
      >
        <Tab label="Manual Entries" value="manual" />
        <Tab label="Payment Challans" value="challans" />
        <Tab label="Fee Calculations" value="fee-calculation" />
      </Tabs>

      {tab === "challans" && (
        <PaymentChallansTab
          challans={challans}
          loading={challansLoading}
          error={challansError}
          page={challansPage}
          rowsPerPage={challansRowsPerPage}
          totalCount={challansTotalCount}
          onChangePage={handleChallansChangePage}
        />
      )}

      {tab === "fee-calculation" && (
        <>
          <FeesCalculationTab
            rows={feeCalcs}
            loading={feeCalcsLoading}
            error={feeCalcsError}
            page={feeCalcsPage}
            rowsPerPage={feeCalcsRowsPerPage}
            totalCount={feeCalcsTotalCount}
            onChangePage={handleFeeCalcsChangePage}
            onEdit={setEditingFeeCalc}
          />
          <EditFeeCalculationDialog
            open={Boolean(editingFeeCalc)}
            entry={editingFeeCalc}
            onClose={() => setEditingFeeCalc(null)}
            onSaved={handleFeeCalcSaved}
          />
        </>
      )}

      {tab === "manual" && (
      <>
      {error && (
        <Typography className={classes.errorText} variant="body2">
          {error}
        </Typography>
      )}

      {loading ? (
        <Box p={4}>
          <CircularCenteredLoader />
        </Box>
      ) : (
        <Paper style={{ width: "100%", overflowX: "auto", boxShadow: "none", border: "1px solid #ddd" }}>
          <TableContainer>
            <Table size="small" style={{ maxWidth: '100%', marginTop: 0 }}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Case ID</TableCell>
                  <TableCell>Sum in Dispute</TableCell>
                  <TableCell>Total Fee</TableCell>
                  <TableCell>Fee Received</TableCell>
                  <TableCell>Received By</TableCell>
                  <TableCell>Date of Receipt</TableCell>
                  <TableCell>Challan No.</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" style={{ padding: "2rem" }}>
                      <Typography color="textSecondary">
                        No financial records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((item, idx) => {
                    const serialNo = totalCount - (page * rowsPerPage + idx);
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell className={classes.tableBodyCell}>{serialNo}</TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {item.case_number || item.tiac_custom_dispute_id || item.dispute_id || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {formatCurrency(item.claim_amount)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {formatCurrency(item.total_fee)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {formatCurrency(item.amount_paid)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {item.received_by?.name || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {safeDateFormat(item.receipt_date) || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {item.challan_number || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <Typography
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              color:
                                item.status === "Paid"
                                  ? "#2e7d32"
                                  : item.status === "In-Deficit"
                                    ? "#c62828"
                                    : "#666",
                            }}
                          >
                            {item.status || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} align="center">
                          <IconButton size="small" onClick={() => onEdit(item)}>
                            <EditOutlined fontSize="small" color="primary" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
          // onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      </>
      )}
    </Box>
  );
}
